/**
 * AngledCarousel — prototype hero treatment.
 *
 * Cards sit on a shallow arc: the further a card is from centre, the smaller,
 * lower and more rotated it gets. Measured off the reference implementation:
 *
 *   offset  0  -> scale 1.00, rotate  0deg, x     0, y    0
 *   offset ±1  -> scale 0.88, rotate ∓2deg, x ±660, y +21
 *   offset ±2  -> scale 0.80, rotate ∓4deg, x ±1320, y +41
 *
 * Two things make it feel smooth, and neither is a CSS transition:
 *  - a single requestAnimationFrame loop eases a fractional `progress` value,
 *    so a drag can interrupt and reverse mid-flight;
 *  - the background gradient is interpolated from that same fractional value,
 *    so at half a swipe the backdrop is exactly half-way between two moods.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useProducts } from '@/context/ProductsContext';
import HeroStatsBand from '@/components/HeroStatsBand';
import HeroProductCard from '@/components/hero/HeroProductCard';
import { usePageTransition, preloadProducts } from '@/App';
import { buildHeroSlides } from '@/data/heroProducts';
import { tebexAtWidth, BACKDROP_WIDTH } from '@/utils/imageSrcSet';
import '@/styles/base/brand-fonts.css';
import '@/styles/components/AngledCarousel.css';

/* Dark, low-saturation grounds so the champagne accent stays the brightest
   thing on screen. Each entry is [inner, outer] of a radial wash. */
const MOODS = {
  scripts: ['#141c28', '#05070b'],
  maps: ['#20180e', '#08060a'],
  peds: ['#1b1124', '#07050b'],
  bundles: ['#0c1c1a', '#04080a'],
  default: ['#17161c', '#06060a'],
};

/* Geometry is viewport-relative so the cards keep the same proportion on a
   13" laptop and a 27" monitor. Ratios measured off the reference: the centre
   card is ~48% of the viewport wide and the cards sit ~51.5% apart, which is
   what leaves exactly one cropped card visible either side. */
const STEP_RATIO = 0.515;
const STEP_MIN = 300;
/* Two ceilings, not one. 1480 was tuned for laptop screens and correct
   there; on a 2200px-plus canvas it left the three visible cards adrift in
   the middle of the stage. The wide ceiling tracks the larger card size the
   stylesheet grants at the same breakpoint. */
const STEP_MAX = 1480;
const STEP_MAX_WIDE = 1960;
const STEP_Y_RATIO = 0.032; // vertical drop per step, as a fraction of the step
const STEP_ROTATE = 2;
const SCALES = [1, 0.88, 0.8];
const SETTLE_MS = 673;
const DRAG_THRESHOLD = 0.18;

/* Flick physics. Release velocity is measured over the last VELOCITY_WINDOW_MS
   of pointer movement and projected forward: a quick flick throws several
   cards, a slow pull just settles on the nearest one. */
const VELOCITY_WINDOW_MS = 110;
const PROJECTION_MS = 340;   // how far ahead the release velocity carries
const MAX_FLICK_CARDS = 6;   // keeps an aggressive swipe from spinning away
const MIN_SETTLE_MS = 300;
const MAX_SETTLE_MS = 1050;

/* Auto-advance interval. Long enough to read a card's name and its line of
   copy without hurrying, short enough that the row is visibly alive. */
const AUTOPLAY_MS = 5500;

/* How many cards either side of the active one get their artwork mounted. */
const NEAR_WINDOW = 3;

const hexToRgb = (hex) => {
  const v = hex.replace('#', '');
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
};

const mixHex = (a, b, t) => {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return `rgb(${A.map((c, i) => Math.round(c + (B[i] - c) * t)).join(',')})`;
};

/* Catalogue categories are plural and capitalised ("Scripts", "Maps"), so
   normalise rather than keying on the raw string. */
const moodFor = (product) => MOODS[String(product?.category || '').toLowerCase()] || MOODS.default;

/* easeOutCubic — fast départ, long settle. Matches the reference's feel. */
const ease = (t) => 1 - Math.pow(1 - t, 3);

const scaleForOffset = (abs) => {
  const i = Math.floor(abs);
  const frac = abs - i;
  const a = SCALES[Math.min(i, SCALES.length - 1)];
  const b = SCALES[Math.min(i + 1, SCALES.length - 1)];
  return a + (b - a) * frac;
};

const AngledCarousel = ({ slides }) => {
  const [active, setActive] = useState(0);
  const total = slides.length;
  const { navigateWithTransition, navigateAsElevator } = usePageTransition();

  const trackRef = useRef(null);
  /* Two stacked backdrop layers cross-fade between the outgoing and incoming
     product art. Only their opacity changes per frame — the image itself is
     swapped just when the index moves, because assigning background-image
     every frame would thrash the decoder. */
  const bgA = useRef(null);
  const bgB = useRef(null);
  const bgIndexA = useRef(-1);
  const bgIndexB = useRef(-1);
  const tintRef = useRef(null);
  const cardRefs = useRef([]);

  /* progress is a fractional slide index; everything renders from it. */
  const progress = useRef(0);
  const target = useRef(0);
  const animStart = useRef(0);
  const animFrom = useRef(0);
  const rafId = useRef(null);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragFrom = useRef(0);
  const animDuration = useRef(SETTLE_MS);
  const velSamples = useRef([]);
  /* Largest horizontal distance travelled during the current gesture, used to
     tell a tap from a swipe when the pointer comes up over a link. */
  const dragMoved = useRef(0);
  /* Auto-advance is suspended while the pointer or keyboard focus is inside
     the hero. A ref rather than state: the interval reads it, and flipping it
     must not re-render every card. */
  const paused = useRef(false);

  /* One source of truth for the step, in px, derived from the current stage
     width — everything (layout, drag mapping, flick velocity) reads this. */
  const stepPx = useCallback(() => {
    const w = trackRef.current?.offsetWidth || window.innerWidth;
    // On a phone one card should own the screen, so the cards sit further
    // apart relative to the viewport than they do on a desktop.
    const ratio = w < 768 ? 0.8 : STEP_RATIO;
    /* matches the stylesheet's card tier at 3000px */
    const cap = w >= 3000 ? STEP_MAX_WIDE : STEP_MAX;
    return Math.max(STEP_MIN, Math.min(cap, w * ratio));
  }, []);

  const wrap = useCallback((i) => ((i % total) + total) % total, [total]);

  /* What was last written to each card, so a frame only touches what moved. */
  const lastPaint = useRef([]);

  const paint = useCallback(() => {
    const p = progress.current;
    /* Read once per frame rather than once per card: it reads offsetWidth,
       and a layout read sitting between style writes is the usual way a
       smooth animation turns expensive. */
    const step = stepPx();

    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      /* Shortest signed distance around the ring. progress is unbounded — it
         keeps climbing as you page on — so this has to be a real modulo, not
         a single correction step, or cards stop wrapping after a lap and a
         half and the carousel runs into empty space. */
      let offset = (((i - p) % total) + total) % total;
      if (offset > total / 2) offset -= total;

      const abs = Math.abs(offset);
      const sign = Math.sign(offset);
      const scale = scaleForOffset(abs);
      const x = offset * step;
      const y = abs * step * STEP_Y_RATIO;
      const rotate = -sign * Math.min(abs, 2) * STEP_ROTATE;

      /* The transform is the only thing that moves every frame. */
      el.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotate}deg) scale(${scale})`;

      /* These three change a few times per slide, and writing them anyway
         invalidated the style of all twenty-seven cards on every frame —
         measured at 25.8 recalculations a frame. */
      const zIndex = String(100 - Math.round(abs * 10));
      const opacity = abs > 2.6 ? '0' : '1';
      const hidden = abs < 0.5 ? 'false' : 'true';
      /* Four steps out, not the 2.6 the opacity uses: gaining or losing a
         layer repaints the card, and that should happen where it cannot be
         seen. */
      const promote = abs <= 4 ? 'transform' : 'auto';
      const was = lastPaint.current[i];
      if (!was || was.z !== zIndex) el.style.zIndex = zIndex;
      if (!was || was.o !== opacity) el.style.opacity = opacity;
      if (!was || was.h !== hidden) {
        el.setAttribute('aria-hidden', hidden);
        el.toggleAttribute('inert', hidden === 'true');
      }
      if (!was || was.w !== promote) el.style.willChange = promote;
      lastPaint.current[i] = { z: zIndex, o: opacity, h: hidden, w: promote };
    });

    /* Backdrop: the centred product's own art, heavily blurred and dimmed so
       it reads as atmosphere rather than a second image competing with the
       card. Cross-faded from the same fractional progress, so half a swipe
       leaves the backdrop exactly half-way between two products. */
    const lo = Math.floor(p);
    const frac = p - lo;
    const iA = wrap(lo);
    const iB = wrap(lo + 1);

    const setLayer = (ref, idxRef, index, opacity) => {
      const el = ref.current;
      if (!el) return;
      if (idxRef.current !== index) {
        idxRef.current = index;
        /* The backdrop is this art blurred by 46px and dimmed; asking the
           proxy for the catalogue's full width put 253KB of unresolvable
           detail on the home page. */
        const art = tebexAtWidth(slides[index]?.thumbnail, BACKDROP_WIDTH);
        el.style.backgroundImage = art ? `url("${art}")` : 'none';
      }
      el.style.opacity = String(opacity);
    };

    setLayer(bgA, bgIndexA, iA, 1 - frac);
    setLayer(bgB, bgIndexB, iB, frac);

    /* A category tint still rides on top so the brand's champagne/graphite
       world survives whatever colours the product art happens to use. */
    if (tintRef.current) {
      const a = moodFor(slides[iA]);
      const b = moodFor(slides[iB]);
      const inner = mixHex(a[0], b[0], frac);
      const outer = mixHex(a[1], b[1], frac);
      tintRef.current.style.background =
        `radial-gradient(120% 90% at 50% 34%, ${inner} 0%, ${outer} 72%)`;
    }
  }, [slides, total, wrap, stepPx]);

  const tick = useCallback(() => {
    const elapsed = performance.now() - animStart.current;
    const t = Math.min(1, elapsed / (animDuration.current || SETTLE_MS));
    progress.current = animFrom.current + (target.current - animFrom.current) * ease(t);
    paint();
    if (t < 1) {
      rafId.current = requestAnimationFrame(tick);
    } else {
      rafId.current = null;
      progress.current = target.current;
      paint();
    }
  }, [paint]);

  const animateTo = useCallback((value, duration) => {
    target.current = value;
    animDuration.current = duration || SETTLE_MS;
    setActive(wrap(Math.round(value)));
    if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }

    /* The motion is driven in JS, so a CSS reduced-motion rule cannot stop it —
       honour the preference here by cutting straight to the destination. */
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      progress.current = value;
      paint();
      return;
    }

    animFrom.current = progress.current;
    animStart.current = performance.now();
    rafId.current = requestAnimationFrame(tick);
  }, [tick, wrap, paint]);

  const go = useCallback((delta) => animateTo(target.current + delta), [animateTo]);

  /* A gesture that moved this far in CSS pixels was a swipe, not a tap, and
     must not follow the link it happened to finish over. Measured in px
     rather than in cards because the threshold is about the hand, not about
     the layout. */
  const CLICK_SLOP = 8;

  /* Navigating from a card. The carousel owns the pointer, so the browser
     still fires a click at the end of a drag that started on the button —
     without this, every swipe begun on "View Product" would leave the page.
     A genuine tap goes through the app's transition helper. */
  const onCardNavigate = (event, to) => {
    if (dragMoved.current > CLICK_SLOP) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (navigateWithTransition) {
      event.preventDefault();
      navigateWithTransition(to);
    }
  };

  /* Where the pointer is over the primary button, as a percentage of its own
     box. Written straight onto the element as custom properties rather than
     held in state: this fires on every pointer move, and a re-render per move
     would be absurd for two numbers that only feed a gradient. */
  const onCtaPointerMove = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--px', `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty('--py', `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  /* The lamp stays on for a moment after the press — a call button that
     goes dark the instant your finger lifts has not registered anything.
     Navigation usually leaves the page before this clears; the timer is for
     the press that slides off the button instead. */
  const [ctaLit, setCtaLit] = useState(false);
  const ctaLitTimer = useRef(null);
  const onCtaPress = () => {
    /* Start fetching the catalogue chunk on touch, not on click. By the time
       the doors are shut the route is usually already in memory, so the ride
       is spent loading rather than stalling. */
    preloadProducts();
    setCtaLit(true);
    window.clearTimeout(ctaLitTimer.current);
    ctaLitTimer.current = window.setTimeout(() => setCtaLit(false), 900);
  };
  useEffect(() => () => window.clearTimeout(ctaLitTimer.current), []);

  /* The href stays real so middle-click, "open in new tab" and the browser's
     own link preview all keep working; the handler only takes over the plain
     left-click it can actually improve on. Without it this anchor was doing a
     full document reload — the slowest navigation on the site. */
  const onCtaClick = (e) => {
    if (!navigateAsElevator) return;
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigateAsElevator('/products');
  };

  const onCtaPointerLeave = (e) => {
    /* Back to the middle, so the highlight settles rather than sticking
       wherever the pointer happened to exit. */
    e.currentTarget.style.setProperty('--px', '50%');
    e.currentTarget.style.setProperty('--py', '50%');
  };

  // Pointer drag: progress follows the finger 1:1, no transition in the way.
  const onPointerDown = (e) => {
    dragging.current = true;
    dragMoved.current = 0;
    dragStartX.current = e.clientX;
    dragFrom.current = progress.current;
    velSamples.current = [{ x: e.clientX, t: performance.now() }];
    if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }
    /* Capture is deliberately NOT taken here. While the stage holds the
       pointer, the browser dispatches the closing click to the stage instead
       of to whatever is under the cursor, so a plain tap on the card's link
       did nothing at all. It is taken on the first real movement instead —
       by which point the gesture is a drag and there is no click to lose. */
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const wasTap = dragMoved.current <= CLICK_SLOP;
    dragMoved.current = Math.max(dragMoved.current, Math.abs(e.clientX - dragStartX.current));
    if (wasTap && dragMoved.current > CLICK_SLOP) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    progress.current = dragFrom.current - (e.clientX - dragStartX.current) / stepPx();
    paint();

    const now = performance.now();
    velSamples.current.push({ x: e.clientX, t: now });
    // Only the tail matters; a long slow drag that ends in a flick should read
    // as a flick, and vice versa.
    velSamples.current = velSamples.current.filter((s) => now - s.t <= VELOCITY_WINDOW_MS);

    // Keep the counter live during the gesture, but only re-render when the
    // nearest card actually changes — not on every frame.
    const nearest = wrap(Math.round(progress.current));
    setActive((prev) => (prev === nearest ? prev : nearest));
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;

    const moved = progress.current - dragFrom.current;

    // Release velocity in cards-per-millisecond, from the tail of the gesture.
    const samples = velSamples.current;
    let velocity = 0;
    if (samples.length >= 2) {
      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last.t - first.t;
      if (dt > 0) velocity = -((last.x - first.x) / stepPx()) / dt;
    }

    const thrown = velocity * PROJECTION_MS;
    const projected = progress.current + thrown;

    let landed = Math.round(projected);
    const startCard = Math.round(dragFrom.current);

    if (Math.abs(moved) <= DRAG_THRESHOLD && Math.abs(thrown) < 0.5) {
      // Barely moved and released gently: snap back, treat it as a mis-tap.
      landed = startCard;
    } else if (landed === startCard) {
      // Committed gesture that rounds back to where it began — push it on.
      landed = startCard + Math.sign(moved || thrown || 1);
    }

    const distance = Math.abs(landed - startCard);
    landed = startCard + Math.sign(landed - startCard) * Math.min(distance, MAX_FLICK_CARDS);

    /* Duration grows with distance but sub-linearly, so a four-card throw
       still feels fast rather than four times as long. */
    const span = Math.max(1, Math.abs(landed - progress.current));
    const duration = Math.min(
      MAX_SETTLE_MS,
      Math.max(MIN_SETTLE_MS, SETTLE_MS * (0.5 + 0.5 * Math.sqrt(span))),
    );

    animateTo(landed, duration);
  };

  useEffect(() => {
    paint();
    return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
  }, [paint]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  /* Auto-advance.
   *
   * Four things have to stop it, and each has a reason. A carousel that keeps
   * moving under the pointer takes the card away as it is being read. One that
   * keeps running in a hidden tab burns battery and comes back having spun
   * through the whole catalogue. One that runs while the visitor is reading
   * further down the page changes what they will find when they scroll back.
   * And a reduced-motion preference means unrequested movement is unwelcome
   * outright, so there it never starts at all. */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const section = trackRef.current?.closest('.angled-hero');
    let onScreen = true;

    const observer = typeof IntersectionObserver === 'function' && section
      ? new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; }, { threshold: 0.25 })
      : null;
    if (observer && section) observer.observe(section);

    const id = setInterval(() => {
      if (paused.current || dragging.current) return;
      if (document.hidden || !onScreen) return;
      /* Lite mode says the same thing prefers-reduced-motion says, and this
         only listened to one of them: a machine that cannot afford the
         effects should not be asked to slide twenty-seven cards every five
         and a half seconds on its own. Read per tick rather than captured, so
         the toggle takes effect without remounting anything. The arrows and
         the drag still work — only the unasked-for movement stops. */
      if (document.documentElement.classList.contains('lite-mode')) return;
      go(1);
    }, AUTOPLAY_MS);

    return () => {
      clearInterval(id);
      if (observer) observer.disconnect();
    };
  }, [go]);

  /* How tall the copy stack really is, published to CSS.
   *
   * The card row is pinned to a fraction of the viewport height, which is a
   * guess about how much room the headline, the line under it and the call
   * button will need. On a phone the headline wraps to three lines and the
   * guess was wrong at every size — the row started 14 to 131 pixels above
   * where the copy ended, so the cards sat on the button. A measurement
   * cannot be wrong about this, and it costs one observer. */
  const copyRef = useRef(null);
  const heroRef = useRef(null);
  /* useLayoutEffect, not useEffect. The stylesheet's fallback height and the
     measured one differ by about 120px, so publishing after the first paint
     meant the section below the hero was drawn once at the fallback and then
     yanked up — measured as a 0.035 layout shift on the home page, the worst
     on the site. Running before paint means the first frame is already right. */
  useLayoutEffect(() => {
    const copy = copyRef.current;
    const hero = heroRef.current;
    if (!copy || !hero || typeof ResizeObserver === 'undefined') return undefined;

    let frame = 0;
    let lastCopy = '';
    let lastCard = '';

    const write = () => {
      frame = 0;
      const c = copy.getBoundingClientRect();
      const h = hero.getBoundingClientRect();
      const copyBottom = `${Math.round(c.bottom - h.top)}px`;

      /* Both the copy and the cards are positioned out of flow, so on the
         phone — where the hero has to be as tall as its contents rather than
         a screen — there is nothing left to give it a height. The tallest
         card does it, since titles run to different lengths and the shortest
         one would let the section end inside the longest. */
      let tallest = 0;
      hero.querySelectorAll('.angled-card').forEach((el) => {
        tallest = Math.max(tallest, el.getBoundingClientRect().height);
      });
      const cardHeight = tallest ? `${Math.ceil(tallest)}px` : lastCard;

      /* Only write when the answer actually changed. Setting a custom
         property that resolves to the same value still dirties style and
         gives the observer something to react to. */
      if (copyBottom !== lastCopy) {
        lastCopy = copyBottom;
        hero.style.setProperty('--copy-bottom', copyBottom);
      }
      if (cardHeight && cardHeight !== lastCard) {
        lastCard = cardHeight;
        hero.style.setProperty('--card-height', cardHeight);
      }
    };

    /* Deferred to the next frame, and never in response to the hero's own
       size.
     *
     * The first version observed the hero and set its height from inside the
     * callback, which is a loop by construction: write, hero resizes, observer
     * fires, write again. The browser reports that as "ResizeObserver loop
     * completed with undelivered notifications" and the dev overlay covers the
     * page with it. The copy and the cards are what the measurement is
     * actually about, and neither of them is resized by what is written. */
    const publish = () => {
      if (frame) return;
      frame = requestAnimationFrame(write);
    };

    write();
    /* The card names set in eight different display faces; until those land
       the names wrap differently and the cards are a different height. One
       more pass when the faces are ready settles it. */
    if (document.fonts?.ready) document.fonts.ready.then(publish).catch(() => {});
    const ro = new ResizeObserver(publish);
    ro.observe(copy);
    hero.querySelectorAll('.angled-card').forEach((el) => ro.observe(el));
    window.addEventListener('resize', publish);
    window.addEventListener('orientationchange', publish);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('resize', publish);
      window.removeEventListener('orientationchange', publish);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="angled-hero"
      aria-roledescription="carousel"
      aria-label="Featured products"
      /* Hovering a card is someone reading it; moving it out from under them
         is the thing that makes auto-advancing carousels irritating. Focus is
         included so keyboard users get the same courtesy. */
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      onFocusCapture={() => { paused.current = true; }}
      onBlurCapture={() => { paused.current = false; }}
    >
      <div className="angled-hero__bg" aria-hidden="true">
        <div className="angled-hero__art" ref={bgA} />
        <div className="angled-hero__art" ref={bgB} />
        <div className="angled-hero__tint" ref={tintRef} />
        <div className="angled-hero__scrim" />
      </div>
      <div className="angled-hero__grain" aria-hidden="true" />

      <div className="angled-hero__copy" ref={copyRef}>
        <h1>
          FiveM Assets. <em>Perfected.</em>
        </h1>
        {/* One line, at every width the shop ships. The sentence this
            replaced ran to 68 characters inside a 46ch measure, so it could
            not have fitted on one line at any size — it wrapped by
            construction. */}
        <p>Scripts, maps and peds. Escrow protected.</p>
        {/* The primary action: a real elevator button, photographed.

            The plate and cap are a photoreal still — aged square brass with a
            black pushbutton — generated, cut out, and trimmed to 560px WebP.
            Everything that makes it a control is still ours: the prismatic
            ring that rotates around the plate, the label engraved on the
            cap, the cap's travel when pressed, and the lamp that comes on
            behind the face and holds. A picture cannot be pressed; this one
            can, because the picture is only the body. */}
        <a
          className={`angled-hero__cta ${ctaLit ? 'is-lit' : ''}`}
          href="/products"
          aria-label="Browse products"
          onPointerMove={onCtaPointerMove}
          onPointerLeave={onCtaPointerLeave}
          onPointerDown={onCtaPress}
          onClick={onCtaClick}
        >
          <span className="angled-hero__cta-rim" aria-hidden="true" />
          <span className="angled-hero__cta-body" aria-hidden="true">
            {/* The fixed panel. This layer never transforms — it is the wall. */}
            <img
              className="angled-hero__cta-plate"
              src="/img/elevator-plate-340.webp"
              srcSet="/img/elevator-plate-340.webp 340w, /img/elevator-plate.webp 560w"
              sizes="(max-width: 768px) 114px, 158px"
              alt=""
              width="560"
              height="560"
              decoding="async"
              draggable="false"
            />
            {/* The recess the cap sits in, so there is somewhere for it to go. */}
            <span className="angled-hero__cta-well" />
            {/* The same photograph again, cropped to the measured cap box. This
                is the only thing that moves, and the label rides with it. */}
            <span className="angled-hero__cta-cap">
              <img
                className="angled-hero__cta-cap-img"
                src="/img/elevator-plate-340.webp"
              srcSet="/img/elevator-plate-340.webp 340w, /img/elevator-plate.webp 560w"
              sizes="(max-width: 768px) 114px, 158px"
                alt=""
                width="560"
                height="560"
                decoding="async"
                draggable="false"
              />
              <span className="angled-hero__cta-lamp" />
              {/* Each word carries itself as data-text so the two colour-split
                  copies behind it can be drawn by the stylesheet alone. */}
              <span className="angled-hero__cta-label">
                <span data-text="BROWSE">BROWSE</span>
                <span data-text="PRODUCTS">PRODUCTS</span>
              </span>
              <span className="angled-hero__cta-sheen" />
            </span>
          </span>
        </a>

        {/* The arrows live inside the copy stack rather than being absolutely
            positioned on their own. Both blocks used to be anchored to the top
            of the section by separate guesses, so making the headline larger
            dropped the button straight onto the arrows. In the flow they
            cannot collide with the copy above them at any size. */}
        <div className="angled-hero__controls">
          <button type="button" onClick={() => go(-1)} aria-label="Previous product">‹</button>
          <span className="angled-hero__counter">
            {String(active + 1).padStart(2, '0')}<i>/</i>{String(total).padStart(2, '0')}
          </span>
          <button type="button" onClick={() => go(1)} aria-label="Next product">›</button>
        </div>
      </div>

      <div
        className="angled-hero__stage"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {slides.map((p, i) => {
          /* Every card starts stacked at the centre before the first paint, so
             a plain loading="lazy" fires for all of them at once — 27 product
             images on first paint. Only mount the artwork for cards near the
             active one; the rest attach theirs as they come round. */
          let dist = Math.abs(((i - active) % total + total) % total);
          if (dist > total / 2) dist = total - dist;
          const near = dist <= NEAR_WINDOW;

          return (
          <article
            className="angled-card"
            key={p.id ?? i}
            ref={(el) => { cardRefs.current[i] = el; }}
          >
            {near
              ? <HeroProductCard product={p} priority={dist === 0} onNavigate={onCardNavigate} />
              : <div className="angled-card__placeholder" aria-hidden="true" />}
          </article>
          );
        })}
      </div>

    </section>
  );
};

const AngledCarouselRoute = () => {
  const { allProducts } = useProducts();
  const slides = useMemo(() => buildHeroSlides(allProducts), [allProducts]);

  if (slides.length < 3) {
    /* English, like the rest of the shop. This one line had been left in
       Turkish and it is the first thing a visitor sees while the catalogue
       is still on its way. */
    return <div className="angled-hero__loading">Loading products…</div>;
  }
  return (
    <>
      <AngledCarousel slides={slides} />
      <HeroStatsBand />
    </>
  );
};

export default AngledCarouselRoute;
